import { get } from 'lodash-es';
import { SetStateAction, useContext } from 'react';

import { AsideExplorer } from '@page/explorer/components/aside-explorer/aside-explorer';
import { ExplorerAuth } from '@page/explorer/components/explorer-auth/explorer-auth';
import { ExplorerQuery } from '@page/explorer/components/explorer-query/explorer-query';

import { Text, Title } from '@atom/typography/typography';
import { MainWrapper, ManiContentLayout } from '@component/layout/main/main-layout.style';
import { LoaderPage } from '@component/loader/loader.page';
import { ColorEnum } from '@enum/style/color.enum';
import { isString } from '@util/data/is-exist';

import { CodeExample } from './components/code-example/code-example';
import { ExplorerBody } from './components/explorer-body/explorer-body';
import { ExplorerHeaders } from './components/explorer-headers/explorer-headers';
import { ExplorerParams } from './components/explorer-params/explorer-params';
import { ExplorerResponse } from './components/explorer-response/explorer-response';
import { ExplorerVarsHeader } from './components/explorer-vars-header/explorer-vars-header';
import { ExplorerContext } from './explorer.provider';
import {
  ColumnLeft,
  ColumnRight,
  ExplorerDescription,
  ExplorerDivider,
  Layout,
  LayoutWrapper,
  Section,
  Wrapper,
} from './explorer.style';
import { useExplorer } from './hooks/use-explorer.hook';
import { useInitialForm } from './hooks/use-initial-form.hook';

export const Explorer = () => {
  const { data, menu, isLoading } = useContext(ExplorerContext);
  const { form, currentApiKey, response, isLoading: isLoadingExplorer, setKey, onCall } = useExplorer(data);
  const { auth, headers, body, params, query, url, method, parentId, name } = useInitialForm(
    data.get(currentApiKey),
    form,
  );

  if (isLoading) return <LoaderPage isDark />;

  const parentDescription =
    get(data.get(currentApiKey), ['request', 'description']) ?? get(data.get(parentId), 'description', '');

  const shouldRenderAuthDivider = body.length + query.length + params.length + headers.length > 0;
  const shouldRenderHeaderDivider = body.length + query.length + params.length > 0;
  const shouldRenderParamsDivider = body.length + query.length > 0;
  const shouldRenderQueryDivider = body.length > 0;

  return (
    <Wrapper>
      <AsideExplorer
        menu={menu}
        selectKey={currentApiKey}
        onClick={(res: { key: SetStateAction<string> }) => setKey(res.key)}
      />
      <Layout>
        <ManiContentLayout isMobileMenuOpen={false}>
          <MainWrapper>
            <LayoutWrapper>
              <ExplorerVarsHeader />
              <Section>
                {isString(currentApiKey) && (
                  <>
                    <ColumnLeft>
                      <Title color={ColorEnum.Black100} level={2} mb={8} weight={400}>
                        {name}
                      </Title>
                      <ExplorerDescription>{parentDescription}</ExplorerDescription>
                      {auth.type !== 'noauth' && (
                        <>
                          <ExplorerAuth form={form} />
                          {shouldRenderAuthDivider && <ExplorerDivider />}
                        </>
                      )}
                      {headers.length > 0 && (
                        <>
                          <ExplorerHeaders fields={headers} form={form} />
                          {shouldRenderHeaderDivider && <ExplorerDivider />}
                        </>
                      )}
                      {params.length > 0 && (
                        <>
                          <ExplorerParams fields={params} form={form} />
                          {shouldRenderParamsDivider && <ExplorerDivider />}
                        </>
                      )}
                      {query.length > 0 && (
                        <>
                          <ExplorerQuery fields={query} form={form} />
                          {shouldRenderQueryDivider && <ExplorerDivider />}
                        </>
                      )}
                      {body.length > 0 && <ExplorerBody fields={body} form={form} />}
                    </ColumnLeft>
                    <ColumnRight>
                      <CodeExample
                        form={form}
                        isLoading={isLoadingExplorer}
                        method={method}
                        url={url}
                        onClick={() => form.validateFields().then(onCall)}
                      />
                      <ExplorerDivider />
                      <ExplorerResponse response={response} />
                    </ColumnRight>
                  </>
                )}
                {!isString(currentApiKey) && (
                  <Text color={ColorEnum.Black10} level='xl'>
                    Choose API to try it
                  </Text>
                )}
              </Section>
            </LayoutWrapper>
          </MainWrapper>
        </ManiContentLayout>
      </Layout>
    </Wrapper>
  );
};
